import React from "react";
import { CreatorFeedVideoPlayer } from "../../../components/FeedPost/PlatformPosts/CreatorFeedVideoPlayer";
import { isLikelyVideoUrl } from "../../../Utils/media/feedMedia";

export type PostGallerySlot = {
  src: string;
  isVideo: boolean;
  poster?: string;
};

function stillSrc(slot: PostGallerySlot): string {
  if (slot.poster && !isLikelyVideoUrl(slot.poster)) return slot.poster;
  if (!slot.isVideo && slot.src && !isLikelyVideoUrl(slot.src)) return slot.src;
  return "/assets/dummy_video.png";
}

function StageVideo({ slot }: { slot: PostGallerySlot }) {
  const thumb = stillSrc(slot);
  return (
    <div
      className="mte-tpl-ph-gallery-stage-media mte-tpl-ph-video"
      style={{ ["--mte-ph-shade" as string]: `url("${thumb}")` }}
    >
      <div className="mte-tpl-ph-video-inner">
        <CreatorFeedVideoPlayer
          media_url={slot.src}
          thumbnail={thumb}
          isSoundOn
          className="creator-feed-card-video hover-play mte-tpl-fc-hover-video mte-tpl-ph-hover-video"
          style={{ width: "100%", height: "100%" }}
        />
      </div>
    </div>
  );
}

export function PostSourceGallery({
  slots,
  layout,
}: {
  slots: PostGallerySlot[];
  layout: string;
}) {
  const [idx, setIdx] = React.useState(0);
  React.useEffect(() => {
    setIdx(0);
  }, [slots.length, layout]);
  if (!slots.length) return null;

  if (layout === "squares") {
    const cols = Math.max(1, Math.ceil(Math.sqrt(slots.length)));
    return (
      <div
        className="mte-tpl-ph-gallery mte-tpl-ph-gallery--squares"
        style={{
          gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
          gridTemplateRows: `repeat(${cols}, minmax(0, 1fr))`,
        }}
      >
        {slots.map((slot, i) => (
          <div key={`${slot.src}-${i}`} className="mte-tpl-ph-gallery-tile">
            <img src={stillSrc(slot)} alt="" />
          </div>
        ))}
      </div>
    );
  }

  if (layout === "stack") {
    return (
      <div className="mte-tpl-ph-gallery mte-tpl-ph-gallery--stack">
        {slots.map((slot, i) => (
          <div key={`${slot.src}-${i}`} className="mte-tpl-ph-gallery-tile">
            <img src={stillSrc(slot)} alt="" />
          </div>
        ))}
      </div>
    );
  }

  if (layout === "filmstrip") {
    const safeIdx = Math.min(idx, slots.length - 1);
    const current = slots[safeIdx];
    const go = (dir: number) =>
      setIdx((i) => (i + dir + slots.length) % slots.length);
    const playing = current.isVideo || isLikelyVideoUrl(current.src);
    return (
      <div className="mte-tpl-ph-gallery mte-tpl-ph-gallery--film">
        <div className="mte-tpl-ph-gallery-stage">
          {playing ? <StageVideo slot={current} /> : <img src={stillSrc(current)} alt="" />}
          {slots.length > 1 ? (
            <>
              <button
                type="button"
                className="mte-tpl-ph-carousel-nav mte-tpl-ph-carousel-nav--prev"
                aria-label="Previous image"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  go(-1);
                }}
              >
                ‹
              </button>
              <button
                type="button"
                className="mte-tpl-ph-carousel-nav mte-tpl-ph-carousel-nav--next"
                aria-label="Next image"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  go(1);
                }}
              >
                ›
              </button>
            </>
          ) : null}
        </div>
        <div className="mte-tpl-ph-gallery-thumbs" role="tablist" aria-label="Post images">
          {slots.map((slot, i) => (
            <button
              key={`${slot.src}-thumb-${i}`}
              type="button"
              role="tab"
              aria-selected={i === safeIdx}
              aria-label={`Image ${i + 1} of ${slots.length}`}
              className={
                i === safeIdx
                  ? "mte-tpl-ph-gallery-thumb is-on"
                  : "mte-tpl-ph-gallery-thumb"
              }
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIdx(i);
              }}
            >
              <img src={stillSrc(slot)} alt="" />
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`mte-tpl-ph-gallery mte-tpl-ph-gallery--mosaic${
        slots.length >= 5 ? " mte-tpl-ph-gallery--many" : ""
      }`}
    >
      {slots.map((slot, i) => (
        <div key={`${slot.src}-${i}`} className="mte-tpl-ph-gallery-tile">
          <img src={stillSrc(slot)} alt="" />
        </div>
      ))}
    </div>
  );
}
